/**
 * useUsers Hook
 *
 * React Query hooks for user management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  usersService,
  type GetUsersQueryParams,
  type CreateUserInput,
  type UpdateUserInput,
  type UpdateUserRoleInput,
  type UpdateModuleRolesInput,
} from '../services/users.service';

/**
 * Query key factory for users
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: GetUsersQueryParams) => [...userKeys.lists(), params] as const,
};

/**
 * Hook to fetch users list with pagination
 */
export function useUsers(params: GetUsersQueryParams) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => usersService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to create a user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserInput) => usersService.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      // Utilisateur cree mais e-mail d'activation non parti : avertissement,
      // pas un succes (il faudra le renvoyer depuis la liste).
      if (result.activationEmailSent === false) {
        toast(result.message, { icon: '⚠️', duration: 10000 });
      } else {
        toast.success(result.message);
      }
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserInput }) =>
      usersService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success('Utilisateur mis a jour');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to update user role
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRoleInput }) =>
      usersService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success('Role mis a jour');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour du role');
    },
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => usersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success('Utilisateur supprime');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to update module roles (US-MAC.8) — bstockRole, bstaffRole, bdisplayRole.
 * Accepte `null` pour retirer l'acces a un module.
 */
export function useUpdateModuleRoles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      brandId,
      input,
    }: {
      userId: string;
      brandId: string;
      input: UpdateModuleRolesInput;
    }) => usersService.updateModuleRoles(userId, brandId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success('Acces module mis a jour');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to resend activation email
 */
export function useResendActivation() {
  return useMutation({
    mutationFn: (id: string) => usersService.resendActivation(id),
    onSuccess: (result) => {
      toast.success(result.message);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'envoi");
    },
  });
}
